<script setup lang="ts">
import type { ISearchState, SearchFilterOptionWithFilterName } from '@/components/Search/types.ts'
import { ref, watch } from 'vue';
import { useI18n } from '@/plugins/i18n';
import SaiModal from '@/components/common/SaiModal/index.ce.vue';
import SelectedFilters from '../SelectedFilters/index.ce.vue'
import { useSearchStore } from '@/components/Search/store/index.ts';
import { formatDate } from '@/utils/dateHelpers';
// emits
const emit = defineEmits(['closeModal', 'saveFilters'])

//props
interface Props {
  display: boolean,
  filterKeys: (keyof ISearchState)[]   // restricts to valid state keys
}

const props = withDefaults(defineProps<Props>(), {
  display: false,
  filterKeys: () => [] as (keyof ISearchState)[]
})

// refs
const updatedFilters = ref<Record<string, SearchFilterOptionWithFilterName[]>>({})

//store
const searchStore = useSearchStore()

//translations
const i18nInstance: any = useI18n()
const { t } = i18nInstance


const closeModal = () => {
  emit('closeModal')
}

// remove filter only in temp
const removeFilterTemporary = (key: keyof ISearchState, item: SearchFilterOptionWithFilterName) => {
  updatedFilters.value[key] = updatedFilters.value[key].filter(i => i !== item)
}

// save: sync temp → store
const saveFilters = () => {
  emit('saveFilters', updatedFilters.value);
}

// copy store → temp when modal opens
watch(
  () => props.display,
  (isOpen) => {
    if (isOpen) {
      const snapshot: Record<any, SearchFilterOptionWithFilterName[]> = {}
      for (const key of props.filterKeys) {
        const value = searchStore[key]
        if (Array.isArray(value)) {
          // If it's already an array, map and add filterName
          snapshot[key] = value.map(item => ({
            ...item,
            filterName: key as string
          })) as SearchFilterOptionWithFilterName[]
        } else if (typeof value === 'string' || value instanceof Date) {
          // If it's a string, create a single-item array
          snapshot[key] = [{
            id: value,
            text: value,
            filterName: key as string
          }]
         } else {
          // fallback to empty array if undefined or unexpected type
          snapshot[key] = []
        }
      }
      updatedFilters.value = snapshot
    }
  },
  { immediate: true }
)

const keysArray = props.filterKeys as Array<keyof ISearchState>
</script>

<template>
  <SaiModal
    :display="display"
    @closeModal="closeModal"
    className="standards-results-all-filters-modal"
  >
    <template #title>All Filters</template>
    <template #body>
      <div class="standards-results-all-filters-body">
        <div class="search-result--selected-filters">
            <template v-for="key in keysArray" :key="key">
              <SelectedFilters v-if="updatedFilters[key]?.length" :selectedFilters="updatedFilters[key]" @removeFilter="item => removeFilterTemporary(key, item)"/>
            </template>
        </div>
      </div>
      <div class="standards-results-all-filters-footer">
        <button class="btn btn-cancel" @click="closeModal">{{ t('cancel') }}</button>
        <button class="btn btn-search" @click="saveFilters">{{ t('save') }}</button>
      </div>
    </template>
  </SaiModal>
</template>